import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressNavigation from '../../components/navigation/ProgressNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import LanguageSelection from './components/LanguageSelection';
import LocationSelection from './components/LocationSelection';
import WorkModeSelection from './components/WorkModeSelection';
import ExperienceVerification from './components/ExperienceVerification';
import IndustrySelection from './components/IndustrySelection';
import ManagementScope from './components/ManagementScope';
import BusinessResponsibility from './components/BusinessResponsibility';
import TechQuestionnaire from './components/TechQuestionnaire';
import ConstructionQuestionnaire from './components/ConstructionQuestionnaire';
import RealEstateQuestionnaire from './components/RealEstateQuestionnaire';
import ProgressSidebar from './components/ProgressSidebar';

const CandidateOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);

  // Form state
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedWorkModes, setSelectedWorkModes] = useState([]);
  const [seniorityLevel, setSeniorityLevel] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [managementLevel, setManagementLevel] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [directReports, setDirectReports] = useState('');
  const [responsibilities, setResponsibilities] = useState([]);
  const [budgetRange, setBudgetRange] = useState('');
  const [revenueImpact, setRevenueImpact] = useState('');
  
  // Industry-specific state
  const [techTrack, setTechTrack] = useState('');
  const [primarySkills, setPrimarySkills] = useState([]);
  const [architectureExperience, setArchitectureExperience] = useState('');
  const [constructionTrack, setConstructionTrack] = useState('');
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectScale, setProjectScale] = useState('');
  const [realEstateTrack, setRealEstateTrack] = useState('');
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [transactionVolume, setTransactionVolume] = useState('');

  const steps = [
    { id: 'language', label: 'Language' },
    { id: 'location', label: 'Location' },
    { id: 'workmode', label: 'Work Mode' },
    { id: 'experience', label: 'Experience' },
    { id: 'industry', label: 'Industry' },
    { id: 'management', label: 'Management' },
    { id: 'business', label: 'Business' },
    { id: 'specialization', label: 'Specialization' }
  ];

  const handleWorkModeToggle = (modeId) => {
    setSelectedWorkModes(prev =>
      prev?.includes(modeId)
        ? prev?.filter(m => m !== modeId)
        : [...prev, modeId]
    );
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return selectedLanguage !== '';
      case 1:
        return selectedCountry !== '' && (selectedCountry === 'remote' || selectedLocation !== '');
      case 2:
        return selectedWorkModes?.length > 0;
      case 3:
        return seniorityLevel !== '' && yearsOfExperience !== '';
      case 4:
        return selectedIndustry !== '';
      case 5:
        return managementLevel !== '' && (managementLevel === 'individual' || (teamSize !== '' && directReports !== ''));
      case 6:
        return responsibilities?.length > 0;
      case 7:
        if (selectedIndustry === 'tech') {
          return techTrack !== '' && primarySkills?.length > 0 && architectureExperience !== '';
        } else if (selectedIndustry === 'construction') {
          return constructionTrack !== '' && projectTypes?.length > 0 && projectScale !== '';
        } else if (selectedIndustry === 'realestate') {
          return realEstateTrack !== '' && propertyTypes?.length > 0 && transactionVolume !== '';
        }
        return false;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      const sectionId = steps?.[currentStep]?.id;
      if (!completedSections?.includes(sectionId)) {
        setCompletedSections([...completedSections, sectionId]);
      }
      
      if (currentStep < steps?.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        navigate('/anonymous-profile-generator');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSectionClick = (stepIndex) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <LanguageSelection
            selectedLanguage={selectedLanguage}
            onLanguageSelect={setSelectedLanguage}
          />
        );
      case 1:
        return (
          <LocationSelection
            selectedCountry={selectedCountry}
            selectedLocation={selectedLocation}
            onCountryChange={setSelectedCountry}
            onLocationChange={setSelectedLocation}
          />
        );
      case 2:
        return (
          <WorkModeSelection
            selectedModes={selectedWorkModes}
            onModeToggle={handleWorkModeToggle}
          />
        );
      case 3:
        return (
          <ExperienceVerification
            seniorityLevel={seniorityLevel}
            onSeniorityChange={setSeniorityLevel}
            yearsOfExperience={yearsOfExperience}
            onYearsChange={setYearsOfExperience}
          />
        );
      case 4:
        return (
          <IndustrySelection
            selectedIndustry={selectedIndustry}
            onIndustrySelect={setSelectedIndustry}
          />
        );
      case 5:
        return (
          <ManagementScope
            managementLevel={managementLevel}
            onManagementLevelChange={setManagementLevel}
            teamSize={teamSize}
            onTeamSizeChange={setTeamSize}
            directReports={directReports}
            onDirectReportsChange={setDirectReports}
          />
        );
      case 6:
        return (
          <BusinessResponsibility
            responsibilities={responsibilities}
            onResponsibilitiesChange={setResponsibilities}
            budgetRange={budgetRange}
            onBudgetRangeChange={setBudgetRange}
            revenueImpact={revenueImpact}
            onRevenueImpactChange={setRevenueImpact}
          />
        );
      case 7:
        if (selectedIndustry === 'tech') {
          return (
            <TechQuestionnaire
              techTrack={techTrack}
              onTechTrackChange={setTechTrack}
              primarySkills={primarySkills}
              onPrimarySkillsChange={setPrimarySkills}
              architectureExperience={architectureExperience}
              onArchitectureExperienceChange={setArchitectureExperience}
            />
          );
        } else if (selectedIndustry === 'construction') {
          return (
            <ConstructionQuestionnaire
              constructionTrack={constructionTrack}
              onConstructionTrackChange={setConstructionTrack}
              projectTypes={projectTypes}
              onProjectTypesChange={setProjectTypes}
              projectScale={projectScale}
              onProjectScaleChange={setProjectScale}
            />
          );
        } else if (selectedIndustry === 'realestate') {
          return (
            <RealEstateQuestionnaire
              realEstateTrack={realEstateTrack}
              onRealEstateTrackChange={setRealEstateTrack}
              propertyTypes={propertyTypes}
              onPropertyTypesChange={setPropertyTypes}
              transactionVolume={transactionVolume}
              onTransactionVolumeChange={setTransactionVolume}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ProgressNavigation
        steps={steps}
        currentStep={currentStep}
        onStepClick={handleSectionClick}
      />
      <div className="flex">
        <ProgressSidebar
          currentStep={currentStep}
          totalSteps={steps?.length}
          completedSections={completedSections}
          onSectionClick={handleSectionClick}
        />

        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6 md:mb-8 lg:hidden">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-muted-foreground">Progress</span>
                <span className="text-sm font-semibold text-primary">
                  {Math.round((currentStep / steps?.length) * 100)}%
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500"
                  style={{ width: `${(currentStep / steps?.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="bg-card rounded-2xl p-6 md:p-8 lg:p-12 elevation-md mb-6">
              {renderStepContent()}
            </div>

            <div className="flex items-center justify-between gap-4">
              <Button
                variant="outline"
                size="lg"
                onClick={handleBack}
                disabled={currentStep === 0}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Back
              </Button>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Shield" size={16} />
                <span>All data encrypted & anonymous</span>
              </div>

              <Button
                variant="default"
                size="lg"
                onClick={handleNext}
                disabled={!validateCurrentStep()}
                iconName={currentStep === steps?.length - 1 ? 'Check' : 'ChevronRight'}
                iconPosition="right"
              >
                {currentStep === steps?.length - 1 ? 'Complete' : 'Continue'}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CandidateOnboarding;